# Manage Enhanced Job Posting drafts

A draft can be saved after the hirer has reviewed their job ad in the Enhanced Job Posting panel.
This draft can then be resumed, queried, previewed, or posted as a job ad to SEEK.

```mermaid Enhanced Job Posting
   sequenceDiagram
   participant Hirer as Hirer
   participant Partner as Partner
   participant Panel as EJP Panel
   participant SEEK API as SEEK API

   Hirer->>Partner: Select Save
   Partner->>Panel: Trigger save event
   Panel->>Partner: Return `draftPositionProfileId`
   opt Resume draft
     Partner->>Panel: Call `render` with `draftPositionProfileId`
   end
   opt Query or preview draft
     Partner->>SEEK API: Query or preview with `draftPositionProfileId`
   end
   opt Post job ad
     Partner->>SEEK API: Call `postPositionFromDraft` with `draftPositionProfileId` and `idempotencyId`
   end
```

## Trigger save of the draft job ad

When the hirer is ready to post or save progress on their current draft, your software should call `dispatchEvent('seek:draft:save')`.
It returns a Promise to a result object.
`dispatchEvent('seek:draft:save')` does not throw or reject.

### Result object

| Result property   | Type      | Description                                                                                                           |
| :---------------- | :-------- | :-------------------------------------------------------------------------------------------------------------------- | ----------------------------------------------------------------------------------------------------------- |
| `isError`         | `boolean` | Whether an error has occurred.                                                                                        |
| `errorCode`       | `string`  | <p>The kind of error. One of `validationFailed` or `mutationFailed`.</p><p>Only present when `isError` is `true`.</p> |
| `errorMessage`    | `string`  | <p>A non-empty message that your software should show to the hirer.</p><p>Only present when `isError` is `true`.</p>  |
| `data`            | `object`  | `null`                                                                                                                | <p>An object that contains information about the draft job ad.</p><p>Present when `isError` is `false`.</p> |
| `data.id`         | `string`  | The ID of the newly persisted immutable draft.                                                                        |
| `data.isComplete` | `boolean` | Whether the draft job ad has all mandatory fields complete and is ready for posting.                                  |

If the save succeeded, then `isError` will be `false`, `data.id` will contain the saved draft’s ID.
After every successful save, replace the stored draft ID with this new ID.
The draft can then be posted with `postPositionFromDraft` if it is create-scoped, or used with `updatePositionProfileFromDraft` if it is edit-scoped.
See [post a draft job ad](/use-cases/job-posting/enhanced/post-a-draft-job-ad) for more information.

An incomplete or invalid form does not create a draft.
It returns `isError: true` with `errorCode` `validationFailed`.

| `errorCode`        | Meaning                                                    |
| ------------------ | ---------------------------------------------------------- |
| `validationFailed` | Visible form data is invalid.                              |
| `mutationFailed`   | The create-draft mutation failed to save the draft job ad. |

`data.id` can be saved alongside the job requisition in your software as a SEEK job ad draft, and used to resume editing later.
If the hirer resumes their posting workflow from a draft, the `id` must be passed as the `draftPositionProfileId` prop to the `render` function.

If there was some problem with saving the draft job ad, then `isError` will be `true`, and `data` will be `null`.
Instructions will be shown within the panel for the hirer to resolve and retry saving,
but if the panel is not in view when the save button is shown consider using `errorMessage` to inform the hirer of errors they should fix.

```javascript
const saveSeekDraftId = async (draftId) => {
  // Replace the stored draft ID alongside the job requisition in your software.
};

const handleSubmitButton = async () => {
  const result = await dispatchEvent('seek:draft:save');

  if (result.isError) {
    // No draft ID will be provided if an error occurs.
    // The panel will display an actionable error message to the hirer.
    return;
  }

  await saveSeekDraftId(result.data.id);
};
```

## Resuming a draft

When the hirer resumes their job posting workflow from a saved draft, your software must pass the saved `draftPositionProfileId` as a top-level prop to the `render` function.
Continue to pass the current job requisition as the `position` prop.
To resume an edit-scoped draft, also pass the live job’s `positionProfileId`.

```javascript
const props = {
  getAuthToken,
  position,
  draftPositionProfileId: savedDraftId
};
SeekApi.render(containerNode, 'enhancedJobPosting', props);
```

Every separate successful save, including a save with unchanged values, returns a new draft ID.
The previous draft ID can be safely discarded.

## Querying draft details

You can retrieve a draft’s details with the `draftPositionProfile` query.
This can be useful if your software displays a summary screen prior to posting the job ad.

```graphql label="Query"
query draftPositionProfile($id: String!) {
  draftPositionProfile(id: $id) {
    seekDraftPositionId {
      value
    }
    seekReadyToPostIndicator
    seekDraftPositionOpening {
      postingRequester {
        id {
          value
        }
      }
      statusCode
    }
    positionTitle
    positionOrganizations {
      id {
        value
      }
      name
    }
    seekHirerJobReference
    seekBillingReference
    positionFormattedDescriptions {
      content
      descriptionId {
        value
      }
    }
    offeredRemunerationPackage {
      basisCode
      descriptions
      ranges {
        intervalCode
        minimumAmount {
          currency
          value
        }
        maximumAmount {
          currency
          value
        }
      }
    }
    seekAnzWorkTypeCode
    jobCategories {
      id {
        value
      }
      name
    }
    positionLocation {
      id {
        value
      }
      name
    }
    seekWorkArrangementCodes
    seekDraftApplicationQuestionnaire {
      components {
        id {
          value
        }
        componentTypeCode
        ... on DraftApplicationQuestion {
          questionHtml
        }
        ... on DraftApplicationPrivacyConsent {
          privacyPolicyUrl {
            url
          }
        }
      }
    }
    seekVideo {
      url
      __typename
    }
    postingInstructions {
      seekAdvertisementProducts {
        id {
          value
        }
      }
      branding {
        id {
          value
        }
        name
      }
      end
      applicationMethods {
        applicationUri {
          url
        }
      }
    }
    seekPartnerMetadata
  }
}
```

```json label="Variables"
{
  "id": "globalPublicTest:positionProfile:draft:seek:2dkY4vZJF"
}
```

```json label="Success result"
{
  "draftPositionProfile": {
    "seekDraftPositionId": {
      "value": "globalPublicTest:positionProfile:draft:seek:2dkY4vZJF"
    },
    "seekReadyToPostIndicator": true,
    "seekDraftPositionOpening": {
      "postingRequester": {
        "id": {
          "value": "seekAnzPublicTest:organization:seek:93WyyF1h"
        }
      },
      "statusCode": "Active"
    },
    "positionTitle": "test title",
    "positionOrganizations": [
      {
        "id": {
          "value": "seekAnzPublicTest:organization:seek:93WyyF1h"
        },
        "name": "Acme Corp"
      }
    ],
    "seekHirerJobReference": null,
    "seekBillingReference": null,
    "positionFormattedDescriptions": [
      {
        "content": "We are looking for a sandwich artist to join our team. You will be responsible for making delicious sandwiches to help our customers seize their day.",
        "descriptionId": {
          "value": "AdvertisementDetails"
        }
      },
      {
        "content": "Become a sandwich artist and make delicious sandwiches to help our customers seize their day.",
        "descriptionId": {
          "value": "SearchSummary"
        }
      }
    ],
    "offeredRemunerationPackage": {
      "basisCode": "Salaried",
      "descriptions": ["Salary is between $5,000 and $100,000 per year."],
      "ranges": [
        {
          "intervalCode": "Year",
          "minimumAmount": {
            "currency": "USD",
            "value": 5000
          },
          "maximumAmount": {
            "currency": "USD",
            "value": 100000
          }
        }
      ]
    },
    "seekAnzWorkTypeCode": "FullTime",
    "jobCategories": [
      {
        "id": {
          "value": "seekAnzPublicTest:jobCategory:seek:2ef7QdMno"
        },
        "name": "Medical Specialists"
      }
    ],
    "positionLocation": [
      {
        "id": {
          "value": "seekAnzPublicTest:location:seek:2dkY4vZJF"
        },
        "name": "Brisbane"
      }
    ],
    "seekWorkArrangementCodes": ["OnSite"],
    "seekDraftApplicationQuestionnaire": {
      "components": []
    },
    "seekVideo": null,
    "postingInstructions": [
      {
        "seekAdvertisementProducts": [
          {
            "id": {
              "value": "globalPublicTest:adProduct:seekApi:61PTyV3XAAuUBE5wCfhuibMZBWHqXhyv1X2KPu1uRjt9"
            }
          }
        ],
        "branding": null,
        "end": null,
        "applicationMethods": []
      }
    ],
    "seekPartnerMetadata": null
  }
}
```

```json label="Error result"
{
  "errors": [
    {
      "message": "Public test tokens are not accepted when accessing live data",
      "locations": [
        {
          "line": 2,
          "column": 3
        }
      ],
      "path": ["draftPositionProfile"],
      "extensions": {
        "code": "UNAUTHENTICATED"
      }
    }
  ]
}
```

## Previewing a draft

You can preview a draft job ad by calling the `postedPositionProfilePreview` query with the `draftPositionProfileId`.
Preview renders the saved draft without mutating the draft or posting a job.

```graphql lovato-ignore-valid-graphql-operation
query ($draftPositionProfileId: String!) {
  postedPositionProfilePreview(
    draftPositionProfileId: $draftPositionProfileId
  ) {
    previewUri {
      url
    }
  }
}
```

```json label="Variables"
{
  "draftPositionProfileId": "globalPublicTest:positionProfile:draft:2ybRBjb6kqDSNAwfxFcqTd"
}
```

```json label="Success result"
{
  "postedPositionProfilePreview": {
    "previewUri": {
      "url": "https://job-ad-preview.seek.com/anzPublicTest?id=seekAnzPublicTest:postedPositionProfilePreview:seekApi:37EWfWxhwVEGTT5BcjfE2L"
    }
  }
}
```

```json label="Error result"
{
  "errors": [
    {
      "message": "No longer valid; refresh the available ad products for this job ad",
      "extensions": {
        "code": "BAD_USER_INPUT",
        "invalidFields": {
          "/positionProfile/postingInstructions/0/seekAdvertisementProductId": "No longer valid; refresh the available ad products for this job ad"
        }
      }
    }
  ]
}
```

For further details on handling job ad previews, refer to [Previewing a job ad].

[Previewing a job ad]: /use-cases/job-posting/managing-job-ads/previewing-a-job-ad

## Draft lifecycle

A draft job ad is created when the hirer saves their job ad within the Enhanced Job Posting panel.
There is no update-in-place operation.
Each subsequent save after the previous save settles, including a save with unchanged values, returns a new draft ID.

A create-scoped draft ID can post one or more new SEEK job ads with `postPositionFromDraft`.
An edit-scoped draft ID can update only its source live job with `updatePositionProfileFromDraft`.

Draft job ads are retained for 180 days from their most recent successful authorized retrieval.

Draft-read failures use these HTTP statuses:

- missing, expired, or otherwise invalid token: `401`
- valid token without the required operation scope: `403`
- valid scoped token with a hirer or partner mismatch: `404`
